import React, { useEffect, useRef, useState } from 'react';
import { Trans } from '@lingui/react';
import classNames from 'classnames';
import styled from 'styled-components';

import Popover, { PopoverPlacement } from '@rover/components/src/callouts/Popover';
import { CLASS_NAMES } from '@rover/constants/src/platform/classNames.constants';

export type Props = {
  placement?: PopoverPlacement;
  children: React.ReactNode;
  message: string | null | undefined;
  className?: string;
  didSubmit?: boolean;
  canDismiss?: boolean;
} & Partial<Omit<React.ComponentProps<typeof Popover>, 'event' | 'content'>>;

export const ErrorWrapper = styled.div`
  color: ${({ theme }) => theme.colors.text.error};
`;

const getClassNames = (message, className) =>
  classNames(className, {
    [CLASS_NAMES.FORM_ERROR_CLASSNAME]: message,
  });

// Static properties moved outside the component to maintain shared state
let globalDidSubmit = true;
let globalCanDismiss = false;

const FormValidationError: React.FC<Props> = ({
  placement = Popover.Placement.TOP,
  message = '',
  children,
  className,
  didSubmit,
  canDismiss,
  ...other
}) => {
  const [lastMessage, setLastMessage] = useState('');
  const [isDismissed, setIsDismissed] = useState(false);
  const popoverRef = useRef<Popover>(null);

  // Set canDismiss on mount if provided
  useEffect(() => {
    if (canDismiss) {
      globalCanDismiss = true;
    }
  }, [canDismiss]);

  // Handle prop changes (equivalent to UNSAFE_componentWillReceiveProps)
  useEffect(() => {
    // Make sure all FormValidationErrors get that there was just a submit
    if (didSubmit) {
      globalDidSubmit = true;
    }

    const isNewSubmission = globalDidSubmit;
    const isNewError = lastMessage !== (message || '');

    // All new submissions and new errors should reset the dismissal
    if (isNewSubmission || isNewError) {
      setLastMessage(message || '');
      setIsDismissed(false);
    }
  }, [message, didSubmit, lastMessage]);

  // Handle click events (equivalent to componentDidMount/componentWillUnmount)
  useEffect(() => {
    const handleClick = (): void => {
      // Dismiss validation messages when clicked.
      if (globalCanDismiss) {
        setIsDismissed(true);
        globalDidSubmit = false;
      }
    };

    if (process.env.JS_ENV_CLIENT) {
      document.addEventListener('click', handleClick, false);
      return () => {
        document.removeEventListener('click', handleClick, false);
      };
    }

    return undefined;
  }, []);

  const renderContent = (): JSX.Element => {
    return (
      <ErrorWrapper role="alert">
        <Trans id={message || lastMessage} />
      </ErrorWrapper>
    );
  };

  const handleClick = (): void => {
    // Dismiss validation messages when clicked.
    if (globalCanDismiss) {
      setIsDismissed(true);
      globalDidSubmit = false;
    }
  };

  return (
    <Popover
      ref={popoverRef}
      // I don't think this is actually doing anything, but I don't want to risk breaking everything
      // during a Typescript conversion ticket
      // @ts-expect-error
      onClick={handleClick}
      event={Popover.Event.NONE}
      placement={placement}
      content={renderContent()}
      open={!!message && !isDismissed}
      display="block"
      className={getClassNames(message, className)}
      {...other}
    >
      {children}
    </Popover>
  );
};

export default FormValidationError;
